/**
 * CampaignCard.jsx — Track B, Phase 10.
 *
 * Actionable per-segment card. Displays the segment identity + a "قصة
 * الشريحة" (one-line story) + a CTA that jumps to the existing campaign
 * scheduler with the segment pre-selected.
 *
 * ISOLATION: pure presentation. The CTA is a `data-testid` hook — the
 * actual wiring to campaign_scheduler happens in Phase 11 when we mount
 * this inside the new customer hub. For now it just fires the provided
 * `onLaunch` prop and logs to console for visual review.
 */
import React from "react";
import { api } from "../lib/api";
import { AudienceBuilderPanel } from "../components/AudienceBuilderPanel";
import { isZeroAudience, ZERO_AUDIENCE_PLACEHOLDER } from "../lib/campaignAudienceGate";
import { zVar } from "../lib/zIndex";
import { InfoTip } from "../components/InfoTip";
import { getSegmentMeta } from "../lib/segmentation";
import { useSegmentsStatus } from "../lib/useSegmentsStatus";

export default function CampaignCard({
  segmentCode,
  count,
  percentage,
  annualSar,        // v121.3 — real annualized SAR from actual pos_sales spend
  annualSarLabel,   // v200.70 — إعلان صريح: صافي / إجمالي / قيمة فائتة / غير محسوب
  isHistorical = false,  // v200.70 — lost/dormant: عرض تاريخي لا مستقبلي
  showProjected = false,  // hide projected annual_sar for team
  onLaunch,
  onDrilldown,
}) {
  useSegmentsStatus(); // إعادة رسم بعد اكتمال تحميل تعريفات الشرائح
  // A3 (البند 3ب) — label/color/story (= description_ar) من تعريفات
  // الخادم مباشرة (getSegmentMeta)؛ لا META محلي بعد اليوم.
  const metaD = getSegmentMeta(segmentCode);
  const meta = { emoji: metaD.label.split(" ")[0] || "❓",
                 label: metaD.label.replace(/^\S+\s*/, "") || segmentCode,
                 color: metaD.color, story: metaD.desc || "—" };
  const isPct = typeof percentage === "number" && !isNaN(percentage);
  const hasSar = showProjected && typeof annualSar === "number" && annualSar > 0;
  // v200.70 — bucketize label into three visual states.
  const labelKind = (annualSarLabel && annualSarLabel.includes("فائتة"))
    ? "historical"
    : (annualSarLabel && annualSarLabel.includes("صافي"))
      ? "net"
      : "unknown";
  // v200.48 — zero-audience gate: any button that produces a real WA
  // send (launch, survey) is disabled here. Uses the shared helper so
  // any policy change lands in all surfaces at once.
  const isEmpty = isZeroAudience(count);
  const [surveyBusy, setSurveyBusy] = React.useState(false);
  const [surveyPreview, setSurveyPreview] = React.useState(null);
  // 2026-10 — بانِي الجمهور الموحَّد: قائمة جوالات نهائية جاهزة (اختيارية)
  // تُستخدَم بدل segment+demographics حين يختارها المستخدم من البانِي.
  // مصدر واحد للجمهور هنا (البند 8) — صندوق DemographicFilters القديم
  // (جنس/عمر منفصل) حُذف من هذه الشاشة؛ نطاقه انتقل بالكامل إلى الباني.
  // 2026-10 — بانِي الجمهور الموحَّد: الواجهة تمرّر *تعريف* الجمهور فقط
  // (segment/gender/age/product/branch/min_visits) — لا قائمة جوالات جاهزة.
  // الخادم (routes/segment_survey.py) يعيد بناء الجمهور بنفسه عبر
  // services.audience_builder.build_from_definition بكل استبعاداته. إصلاح
  // أمني 2026-10: كانت تُرسَل قائمة جوالات جاهزة (manual_phones) بلا إعادة
  // فرض الاستبعادات على الخادم — ثغرة تجاوز.
  const [audienceDefinition, setAudienceDefinition] = React.useState(null);
  const [audienceCount, setAudienceCount] = React.useState(0);
  const clearManualPhones = () => { setAudienceDefinition(null); setAudienceCount(0); };

  const handle = () => {
    if (isEmpty) return;
    if (typeof onLaunch === "function") onLaunch(segmentCode);
    else if (typeof window !== "undefined" && window.console) {
      window.console.info("[CampaignCard] launch requested for", segmentCode);
    }
  };
  const handleDrill = () => {
    if (typeof onDrilldown === "function") onDrilldown(segmentCode);
  };
  // v200.48 — survey button: preview FIRST (mandatory per rule 10),
  // then the caller confirms in the modal, which POSTs a second time
  // with dry_run=false + confirmed=true. All layers enforced in the
  // backend endpoint /api/segments/survey-blast.
  const handleSurvey = async (overrideDefinition) => {
    if (isEmpty || surveyBusy) return;
    setSurveyBusy(true);
    try {
      const defToUse = overrideDefinition !== undefined ? overrideDefinition : audienceDefinition;
      const { data } = await api.post("/segments/survey-blast", {
        segment: segmentCode, dry_run: true, confirmed: false,
        audience_definition: defToUse || undefined,
      });
      setSurveyPreview(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "خطأ";
      setSurveyPreview({ error: String(detail).slice(0, 200) });
    } finally { setSurveyBusy(false); }
  };
  const handleBuilderApply = (phones, definition, counts) => {
    setAudienceDefinition(definition);
    setAudienceCount(counts?.final ?? (phones || []).length);
    handleSurvey(definition);
  };
  const confirmSurvey = async () => {
    setSurveyBusy(true);
    try {
      const { data } = await api.post("/segments/survey-blast", {
        segment: segmentCode, dry_run: false,
        confirmed: true, approved_by_owner: "hub_user",
        audience_definition: audienceDefinition || undefined,
      });
      setSurveyPreview({ ...data, _sent: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "خطأ";
      setSurveyPreview((p) => ({ ...(p || {}), error: String(detail).slice(0, 200) }));
    } finally { setSurveyBusy(false); }
  };

  return (
    <article
      dir="rtl"
      data-testid={`campaign-card-${segmentCode}`}
      style={{
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Noto Naskh Arabic', sans-serif",
        background: "#FFFFFF",
        border: `1px solid ${meta.color}55`,
        borderRadius: 18, padding: "20px 22px",
        color: "#3D2A1C", display: "grid", gap: 14, position: "relative",
        overflow: "hidden",
        boxShadow: "0 6px 20px rgba(61,42,28,0.06)",
      }}
    >
      <div aria-hidden style={{
        position: "absolute", top: -30, left: -30, width: 120, height: 120,
        background: `${meta.color}18`, borderRadius: "50%", filter: "blur(30px)",
        pointerEvents: "none",
      }} />

      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div aria-hidden style={{
            width: 48, height: 48, borderRadius: 14,
            background: `${meta.color}22`, display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: 24,
          }}>
            {meta.emoji}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: -0.3 }}>
              {meta.label}
            </div>
            <div data-testid={`campaign-card-count-${segmentCode}`}
                 style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>
              {Number(count || 0).toLocaleString("en-US")} عميل
              {isPct ? ` · ${percentage.toFixed(1)}%` : ""}
            </div>
            {hasSar && (
              <div
                data-testid={`campaign-card-sar-${segmentCode}`}
                style={{ fontSize: 12, fontWeight: 700, marginTop: 4, color: meta.color }}
              ><InfoTip text={
                  labelKind === "historical"
                    ? "قيمة فائتة صافية — ما كان العميل ينفقه قبل غيابه (مسنَّد إلى سنة كاملة). صافٍ بلا ضريبة عبر tenant_settings."
                    : "إسقاط رياضي من إنفاق آخر 90 يوم × (365/90)، صافٍ بلا ضريبة."
                } />
                {labelKind === "historical" ? "≈ " : "≈ "}
                {Number(annualSar).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                {" ريال/سنة"}
                <span
                  data-testid={`campaign-card-sar-label-${segmentCode}`}
                  style={{
                    display: "inline-flex", alignItems: "center",
                    marginInlineStart: 6, padding: "1px 6px", borderRadius: 999,
                    background: labelKind === "historical" ? "#FEF3C7"
                              : labelKind === "net"        ? "#ECFDF5"
                                                            : "#F3F4F6",
                    color:      labelKind === "historical" ? "#78350F"
                              : labelKind === "net"        ? "#065F46"
                                                            : "#374151",
                    border:     labelKind === "historical" ? "1px solid #FBBF24"
                              : labelKind === "net"        ? "1px solid #10B981"
                                                            : "1px solid #D1D5DB",
                    fontSize: 9, fontWeight: 700,
                  }}
                >
                  {annualSarLabel || "غير محسوب"}
                </span>
              </div>
            )}
            {showProjected && !hasSar && (segmentCode === "lost" || segmentCode === "dormant") && (
              <div
                data-testid={`campaign-card-sar-empty-${segmentCode}`}
                style={{ fontSize: 11, fontWeight: 600, marginTop: 4, color: "#78716C" }}
 
              ><InfoTip text="هذه الشريحة معرَّفة بالغياب — لا إسقاط مستقبلي حين لا توجد بيانات إنفاق تاريخية كافية." />
                لا ينطبق — {segmentCode === "lost"
                  ? "العميل غاب ≥ 91 يوماً"
                  : "العميل خامل والاتجاه سالب"}
              </div>
            )}
          </div>
        </div>
      </header>

      <p style={{
        margin: 0, fontSize: 13.5, color: "#57534E", lineHeight: 1.6,
        position: "relative",
      }}>
        {meta.story}
      </p>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid={`campaign-card-drilldown-${segmentCode}`}
          onClick={handleDrill}
          style={{
            border: `1px solid ${meta.color}66`, cursor: "pointer",
            padding: "10px 14px", borderRadius: 12,
            background: "#FFFFFF", color: meta.color,
            fontWeight: 700, fontSize: 12,
          }}
        >
          👥 عرض العملاء
        </button>
        <button
          type="button"
          data-testid={`campaign-card-launch-${segmentCode}`}
          onClick={handle}
          disabled={isEmpty}
 
          style={{
            border: "none", cursor: isEmpty ? "not-allowed" : "pointer",
            padding: "10px 16px", borderRadius: 12,
            background: isEmpty ? "#9CA3AF" : meta.color, color: "#FFFFFF",
            fontWeight: 700, fontSize: 13, letterSpacing: 0.2,
            transition: "transform 150ms ease, filter 150ms ease",
            textShadow: "0 1px 2px rgba(0,0,0,0.15)",
            opacity: isEmpty ? 0.6 : 1,
          }}
          onMouseDown={(e) => { if (!isEmpty) e.currentTarget.style.transform = "scale(0.97)"; }}
          onMouseUp={(e) => { if (!isEmpty) e.currentTarget.style.transform = "scale(1)"; }}
          onMouseLeave={(e) => { if (!isEmpty) e.currentTarget.style.transform = "scale(1)"; }}
         aria-label={isEmpty ? "لا جمهور في هذه الشريحة — الزر معطَّل" : ""}>
          {isEmpty ? `إطلاق حملة (${ZERO_AUDIENCE_PLACEHOLDER})` : "إطلاق حملة →"}
        </button>
        {/* v200.48 — survey blast button. Disabled on zero audience.
             Preview-first (rule 10). Every send goes through
             /api/segments/survey-blast which routes wa_gate +
             enforce_bulk_confirmation + kill_switch. */}
        <button
          type="button"
          data-testid={`campaign-card-survey-${segmentCode}`}
          onClick={() => handleSurvey()}
          disabled={isEmpty || surveyBusy}
 
          style={{
            border: `1px solid ${meta.color}66`,
            cursor: (isEmpty || surveyBusy) ? "not-allowed" : "pointer",
            padding: "10px 14px", borderRadius: 12,
            background: "rgba(255,255,255,0.9)",
            color: isEmpty ? "#9CA3AF" : meta.color,
            fontWeight: 700, fontSize: 12,
            opacity: isEmpty ? 0.55 : 1,
          }}
         aria-label={isEmpty ? "لا جمهور — لا استبيان" : "أرسل استبيان لهذه الشريحة"}>
          {surveyBusy ? "⏳" : `📋 استبيان${isEmpty ? ` (${ZERO_AUDIENCE_PLACEHOLDER})` : ""}`}
        </button>
      </div>
      {/* Preview modal — must show recipient count + survey text +
           source badge before any real send (rule 10). */}
      {surveyPreview && (
        <div
          data-testid={`survey-preview-${segmentCode}`}
          role="dialog"
          onClick={() => setSurveyPreview(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: zVar('extra_1000'), padding: 16,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            maxWidth: 480, width: "100%", background: "#fff",
            borderRadius: 14, padding: 20,
            fontFamily: "'El Messiri', sans-serif", color: "#3D2A1C",
          }} dir="rtl">
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 10 }}>
              <b style={{ fontSize: 15 }}>📋 معاينة إرسال الاستبيان</b>
              <button onClick={() => setSurveyPreview(null)}
                data-testid={`survey-preview-close-${segmentCode}`}
                style={{ border: "none", background: "transparent",
                          fontSize: 20, cursor: "pointer", color: "#78716C" }}>
                ✕
              </button>
            </div>
            {surveyPreview.error && (
              <div style={{ padding: 10, background: "rgba(220,38,38,0.10)",
                             color: "#B91C1C", borderRadius: 8, fontSize: 12 }}>
                ⚠️ {surveyPreview.error}
              </div>
            )}
            {surveyPreview._sent && (
              <div style={{ padding: 10, background: "rgba(16,185,129,0.10)",
                             color: "#047857", borderRadius: 8, fontSize: 12,
                             fontWeight: 700 }}>
                ✓ أُرسل: {surveyPreview.sent} · فشل: {surveyPreview.failed}
              </div>
            )}
            {!surveyPreview._sent && !surveyPreview.error && (
              <>
                {/* 2026-10 — بانِي الجمهور الموحَّد: مصدر واحد للجمهور في هذه
                     الشاشة (البند 8) — صندوق الفلاتر الديموغرافية القديم
                     (جنس/عمر منفصل) حُذف؛ الجنس والعمر وشمول غير المعروف
                     أصبحت جميعها هنا فقط. */}
                <AudienceBuilderPanel screenName="survey" fixedSegment={segmentCode}
                  onApply={handleBuilderApply} />
                {audienceDefinition && (
                  <div data-testid={`survey-manual-phones-active-${segmentCode}`}
                       style={{ padding: 8, marginTop: 8, background: "#ECFDF5", color: "#047857",
                                 borderRadius: 8, fontSize: 11.5, display: "flex",
                                 justifyContent: "space-between", alignItems: "center" }}>
                    <span>✓ جمهور البانِي مفعَّل ({audienceCount} جوال) — يتجاوز الشريحة/الفلاتر أعلاه</span>
                    <button type="button" data-testid={`survey-manual-phones-clear-${segmentCode}`}
                      onClick={clearManualPhones}
                      style={{ border: "none", background: "transparent", color: "#047857",
                                fontWeight: 700, cursor: "pointer" }}>✖ إلغاء</button>
                  </div>
                )}
                <div data-testid={`survey-preview-recipients-${segmentCode}`}
                     style={{ padding: 10, background: "#F9FAFB",
                                borderRadius: 8, fontSize: 13, marginBottom: 8 }}>
                  <b>عدد المستلمين الفعلي:</b>{" "}
                  {Number(surveyPreview.audience_count || 0).toLocaleString("en-US")} عميل
                  {" · مستبعَد "}
                  {Number(surveyPreview.excluded_count || 0).toLocaleString("en-US")}
                </div>
                <div data-testid={`survey-preview-source-${segmentCode}`}
                     style={{ padding: 10, background: "rgba(16,185,129,0.08)",
                                color: "#047857", borderRadius: 8, fontSize: 11.5,
                                marginBottom: 8, fontWeight: 700 }}>
                  🛰 المصدر: {surveyPreview.source_label || surveyPreview.source} · نافذة 90ي
                </div>
                <div style={{ padding: 10, background: "#FFFBEB", borderRadius: 8,
                                fontSize: 12, marginBottom: 12,
                                border: "1px solid rgba(217,119,6,0.35)" }}>
                  <b>نصّ الاستبيان:</b>{" "}
                  قالب Meta:{" "}
                  <code style={{ background: "#fff", padding: "2px 6px",
                                   borderRadius: 4, direction: "ltr",
                                   display: "inline-block" }}>
                    {surveyPreview.template_name}
                  </code>
                  {" · اللغة: "}{surveyPreview.template_language}
                </div>
                <button
                  type="button"
                  data-testid={`survey-preview-confirm-${segmentCode}`}
                  onClick={confirmSurvey}
                  disabled={surveyBusy || surveyPreview.status === "zero_audience_blocked"}
                  style={{
                    width: "100%", border: "none",
                    padding: "12px 18px", borderRadius: 10,
                    background: surveyPreview.status === "zero_audience_blocked"
                                ? "#9CA3AF" : "#0EA5E9",
                    color: "#fff", fontWeight: 800, fontSize: 13,
                    cursor: (surveyBusy || surveyPreview.status === "zero_audience_blocked")
                            ? "not-allowed" : "pointer",
                  }}
                >
                  {surveyBusy ? "⏳ يُرسل..."
                    : surveyPreview.status === "zero_audience_blocked"
                      ? "لا جمهور — لا إرسال"
                      : "✓ أؤكّد الإرسال إلى الجمهور أعلاه"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
